import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import PortalLayout from '../components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Send, Briefcase, MapPin, DollarSign, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OfferCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const candidateId = searchParams.get('candidateId');
  
  const [candidate, setCandidate] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [formData, setFormData] = useState({
    role_title: '',
    location_suburb: '',
    location_state: 'NSW',
    work_arrangement: 'on_site',
    work_type: 'full_time',
    salary_min: '',
    salary_max: '',
    salary_unit: 'year',
    start_date: '',
    message: ''
  });

  useEffect(() => {
    if (candidateId) {
      fetchCandidate();
    }
  }, [candidateId]);

  const fetchCandidate = async () => {
    try {
      const response = await axios.get(`${API}/portal/talent/candidate/${candidateId}`);
      setCandidate(response.data.candidate);
    } catch (error) {
      toast.error('Failed to load candidate');
      navigate('/portal/talent');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!candidateId) {
      toast.error('Candidate ID required');
      return;
    }
    
    setSending(true);
    
    try {
      const offerData = {
        candidate_user_id: candidateId,
        role_title: formData.role_title,
        location_suburb: formData.location_suburb,
        location_state: formData.location_state,
        work_arrangement: formData.work_arrangement,
        work_type: formData.work_type,
        salary_min: parseFloat(formData.salary_min) || null,
        salary_max: parseFloat(formData.salary_max) || null,
        salary_unit: formData.salary_unit,
        start_date: formData.start_date || null,
        message: formData.message
      };
      
      await axios.post(`${API}/portal/talent/offers`, offerData);
      
      toast.success('Offer sent successfully!');
      navigate('/portal/talent/offers');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer');
    } finally {
      setSending(false);
    }
  };

  return (
    <PortalLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Send Job Offer</h1>
          {candidate && (
            <p className="text-muted-foreground">
              Sending offer to {candidate.display_name}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Offer Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Role Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.role_title}
                  onChange={(e) => setFormData({ ...formData, role_title: e.target.value })}
                  placeholder="e.g., Senior Software Engineer"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Location</label>
                  <Input
                    value={formData.location_suburb}
                    onChange={(e) => setFormData({ ...formData, location_suburb: e.target.value })}
                    placeholder="Sydney"
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">State</label>
                  <select
                    value={formData.location_state}
                    onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="NSW">NSW</option>
                    <option value="VIC">VIC</option>
                    <option value="QLD">QLD</option>
                    <option value="WA">WA</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Work Arrangement</label>
                  <select
                    value={formData.work_arrangement}
                    onChange={(e) => setFormData({ ...formData, work_arrangement: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="on_site">On-site</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="remote">Remote</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Work Type</label>
                  <select
                    value={formData.work_type}
                    onChange={(e) => setFormData({ ...formData, work_type: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="full_time">Full-time</option>
                    <option value="part_time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="casual">Casual</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Salary Range</label>
                <div className="grid md:grid-cols-3 gap-4">
                  <Input
                    type="number"
                    value={formData.salary_min}
                    onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                    placeholder="Min"
                  />
                  <Input
                    type="number"
                    value={formData.salary_max}
                    onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                    placeholder="Max"
                  />
                  <select
                    value={formData.salary_unit}
                    onChange={(e) => setFormData({ ...formData, salary_unit: e.target.value })}
                    className="px-3 py-2 border border-border rounded-lg bg-background"
                  >
                    <option value="year">Per Year</option>
                    <option value="hour">Per Hour</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Start Date (Optional)</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-2">
                  Message to Candidate <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Explain why you're interested in this candidate and what makes this opportunity exciting..."
                  rows={6}
                  required
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  💡 This offer will expire in 7 days. The candidate can accept, decline, or ask questions.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/portal/talent/${candidateId}`)}
              className="flex-1 px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Send className="w-5 h-5" />
              {sending ? 'Sending...' : 'Send Offer'}
            </button>
          </div>
        </form>
      </div>
    </PortalLayout>
  );
};

export default OfferCreate;
